import { UserRole } from "@features/settings/types/settings.types";
import { ConfirmationModal } from "@shared/components/ConfirmationModal";
import { getProfileImage, useProfile } from "@shared/hooks/useProfile";
import theme from "@shared/utils/themeColors";
import { formatDistanceToNow } from "date-fns";
import { useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { Link } from "react-router-dom";
import { useEventsStore } from "../store/useEventsStore";
import { EventCommentItemProps } from "../types/event.types";
import EventCommentForm from "./EventCommentForm";

const EventCommentItem: React.FC<EventCommentItemProps> = ({
  eventId,
  comment,
  onReply,
  isReplyActive,
  onMention,
  getCommentsByParentId,
  getLimitedComments,
  hasMoreComments,
  loadMoreComments,
  nestingLevel = 0,
}: EventCommentItemProps) => {
  const { toggleCommentUpvote, addComment, deleteComment } = useEventsStore();
  const { user, profile } = useProfile();
  const [upvotes, setUpvotes] = useState(comment.upvotes || 0);
  const [isUpvoted, setIsUpvoted] = useState(comment.isUpvoted || false);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [replyContent, setReplyContent] = useState("");
  const [isSubmittingReply, setIsSubmittingReply] = useState(false);

  // Delete confirmation modal
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Check if user can delete this comment (author or admin)
  const canDelete =
    profile &&
    ((comment.author && profile.id === comment.author.id) ||
      user.role === UserRole.ADMIN);

  const commenters = useMemo(() => {
    return getCommentsByParentId(undefined)
      .map((comment) => comment.author)
      .filter((author): author is { id: string } => !!author)
      .filter(
        (author, index, self) =>
          index === self.findIndex((a) => a && a.id === author.id)
      );
  }, [getCommentsByParentId]);

  const handleUpvote = async () => {
    if (!comment.id) return;
    try {
      if (isUpvoted) {
        setUpvotes((prev) => Math.max(0, prev - 1));
      } else {
        setUpvotes((prev) => prev + 1);
      }
      setIsUpvoted(!isUpvoted);
      const result = await toggleCommentUpvote(eventId, comment.id);
      if (!result.success) {
        if (isUpvoted) {
          setUpvotes((prev) => prev + 1);
        } else {
          setUpvotes((prev) => Math.max(0, prev - 1));
        }
        setIsUpvoted(isUpvoted);
      }
    } catch (error) {
      console.error("Error toggling comment upvote:", error);
      if (isUpvoted) {
        setUpvotes((prev) => prev + 1);
      } else {
        setUpvotes((prev) => Math.max(0, prev - 1));
      }
      setIsUpvoted(isUpvoted);
    }
  };

  const handleReplyClick = () => {
    setShowReplyForm(true);
    if (comment.id) {
      onReply(comment.id);
    }
  };

  const canReply = nestingLevel < 3;

  const handleReplySubmit = async () => {
    if (!comment.id || !eventId || !replyContent.trim()) return;
    try {
      setIsSubmittingReply(true);
      const newComment = await addComment(eventId, replyContent, comment.id);
      if (newComment) {
        setReplyContent("");
        setShowReplyForm(false);
        onReply(undefined);
        if (newComment.id) {
          setTimeout(() => {
            const commentElement = document.getElementById(
              `event-comment-${newComment.id}`
            );
            if (commentElement) {
              commentElement.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });
              commentElement.classList.add(`${theme.bg.accent}`);
              setTimeout(() => {
                commentElement.classList.remove(`${theme.bg.accent}`);
              }, 2000);
            }
          }, 300);
        }
      }
    } catch (error) {
      console.error("Error adding reply:", error);
    } finally {
      setIsSubmittingReply(false);
    }
  };

  const handleCancelReply = () => {
    setShowReplyForm(false);
    setReplyContent("");
    onReply(undefined);
  };

  // Handle delete comment
  const handleDeleteClick = () => {
    setShowDeleteConfirmation(true);
  };

  const handleDeleteConfirm = async () => {
    if (!comment.id || !eventId) return;

    setIsDeleting(true);
    try {
      const result = await deleteComment(eventId, comment.id);
      if (result.success) {
        toast.success("Comment deleted successfully");
        setShowDeleteConfirmation(false);
      } else {
        toast.error("Failed to delete comment");
      }
    } catch (error) {
      console.error("Error deleting comment:", error);
      toast.error("Failed to delete comment");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirmation(false);
  };

  return (
    <li className="relative pl-9 space-y-5">
      <div className="flex items-start">
        {/* <div className="absolute top-0 left-0">
          <button
            className={`text-xs font-semibold text-left w-6 rounded-sm flex flex-col justify-center items-center ${
              isUpvoted
                ? `${theme.text.brand}`
                : `${theme.text.secondary} hover:${theme.text.brand}`
            }`}
            onClick={handleUpvote}
          >
            <svg
              className={`inline-flex ${
                isUpvoted ? theme.bg.fill : "fill-gray-400 dark:fill-gray-500"
              } mt-1.5 mb-1.5`}
              width="12"
              height="6"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="m0 6 6-6 6 6z" />
            </svg>
            <div>{upvotes}</div>
          </button>
        </div> */}
        <div>
          <div className={`grow text-sm ${theme.text.primary} space-y-2 mb-2`}>
            <p>{comment.content}</p>
          </div>
          <div className="flex flex-wrap text-xs">
            <div className="flex items-center after:block after:content-['·'] last:after:content-[''] after:text-sm after:text-gray-400 dark:after:text-gray-600 after:px-2">
              {comment.author ? (
                <>
                  <Link
                    className="block mr-2"
                    to={`/community/profile/${comment.author.id}`}
                  >
                    <img
                      className="rounded-full h-8 w-8"
                      src={getProfileImage(comment.author)}
                      alt={comment.author.name || "User"}
                    />
                  </Link>
                  <Link
                    className={`font-medium ${theme.text.brand} ${theme.text.brandHover}`}
                    to={`/community/profile/${comment.author.id}`}
                  >
                    {comment.author.name || "Anonymous"}
                  </Link>
                </>
              ) : (
                <span className="italic text-gray-400">Unknown user</span>
              )}
            </div>
            {canReply && (
              <div
                className={`flex items-center after:block after:content-['·'] last:after:content-[''] after:text-sm after:${theme.text.inactive} after:px-2`}
              >
                <button
                  className={`font-medium ${theme.text.muted} ${theme.text.mutedHover}`}
                  onClick={handleReplyClick}
                >
                  Reply
                </button>
              </div>
            )}
            {canDelete && (
              <div
                className={`flex items-center after:block after:content-['·'] last:after:content-[''] after:text-sm after:${theme.text.inactive} after:px-2`}
              >
                <button
                  className={`font-medium text-red-500 hover:text-red-600`}
                  onClick={handleDeleteClick}
                >
                  Delete
                </button>
              </div>
            )}
            <div className="flex items-center">
              <span className={`${theme.text.muted}`}>
                {comment.createdAt instanceof Date
                  ? formatDistanceToNow(comment.createdAt, { addSuffix: true })
                  : formatDistanceToNow(new Date(comment.createdAt?.toDate()), {
                      addSuffix: true,
                    })}
              </span>
            </div>
          </div>
        </div>
      </div>
      {showReplyForm && (
        <div className="mt-3 ml-6">
          <EventCommentForm
            profile={profile}
            commentContent={replyContent}
            setCommentContent={setReplyContent}
            onSubmit={handleReplySubmit}
            isSubmitting={isSubmittingReply}
            parentComment={comment}
            onCancel={handleCancelReply}
            availableUsers={commenters}
            compact={true}
          />
        </div>
      )}
      {comment.replies && comment.replies.length > 0 && (
        <ul className="mt-4 space-y-5 ml-6">
          {comment.replies.map((reply) => (
            <div
              key={reply.id}
              id={`event-comment-${reply.id}`}
              className="transition-colors duration-500"
            >
              <EventCommentItem
                eventId={eventId}
                comment={reply}
                onReply={onReply}
                isReplyActive={isReplyActive}
                onMention={onMention}
                getCommentsByParentId={getCommentsByParentId}
                getLimitedComments={getLimitedComments}
                hasMoreComments={hasMoreComments}
                loadMoreComments={loadMoreComments}
                nestingLevel={nestingLevel + 1}
              />
            </div>
          ))}
        </ul>
      )}
      {(!comment.replies || comment.replies.length === 0) &&
        getLimitedComments(comment.id).length > 0 && (
          <ul className="mt-4 space-y-5 ml-6">
            {getLimitedComments(comment.id).map((reply) => (
              <div
                key={reply.id}
                id={`event-comment-${reply.id}`}
                className="transition-colors duration-500"
              >
                <EventCommentItem
                  eventId={eventId}
                  comment={reply}
                  onReply={onReply}
                  isReplyActive={isReplyActive}
                  onMention={onMention}
                  getCommentsByParentId={getCommentsByParentId}
                  getLimitedComments={getLimitedComments}
                  hasMoreComments={hasMoreComments}
                  loadMoreComments={loadMoreComments}
                  nestingLevel={nestingLevel + 1}
                />
              </div>
            ))}
            {hasMoreComments(comment.id) && (
              <div className="mt-1 text-center">
                <button
                  className={`text-sm font-medium ${theme.text.brand} ${theme.text.brandHover}`}
                  onClick={() => loadMoreComments(comment.id)}
                >
                  Show more replies
                </button>
              </div>
            )}
          </ul>
        )}
      {/* Delete Confirmation Modal */}
      {showDeleteConfirmation && (
        <ConfirmationModal
          isOpen={showDeleteConfirmation}
          title="Delete Comment"
          message="Are you sure you want to delete this comment? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          onConfirm={handleDeleteConfirm}
          onClose={handleDeleteCancel}
          isLoading={isDeleting}
        />
      )}
    </li>
  );
};

export default EventCommentItem;
